import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { databaseApi } from "@/lib/api";
import { DatabaseDetails, ConnectionStats } from "@/types/database";
import {
  RefreshCw,
  XCircle,
  Trash2,
  Loader2,
  Database,
  Users,
  Clock,
  Activity,
} from "lucide-react";

interface ConnectionManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  database: DatabaseDetails | null;
}

/**
 * Connection Management Dialog Component
 * Displays connection statistics and provides actions to manage database connections
 */
const ConnectionManagementDialog: React.FC<ConnectionManagementDialogProps> = ({
  isOpen,
  onClose,
  database,
}) => {
  const { toast, ToastContainer } = useToast();
  const [stats, setStats] = useState<ConnectionStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  /**
   * Fetches connection statistics from the API
   */
  const fetchConnectionStats = async () => {
    if (!database) return;

    setIsLoading(true);
    try {
      const response = await databaseApi.getConnectionStats(database.dbname);
      if (response.success && response.data) {
        setStats(response.data.stats);
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to fetch connection statistics",
          status: "error",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch connection statistics",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Closes all connections to the database
   */
  const handleCloseAllConnections = async () => {
    if (!database) return;

    const confirmed = window.confirm(
      `Are you sure you want to close all ${stats?.total || 0} active connections to "${database.dbname}"? This may interrupt active operations.`
    );

    if (!confirmed) return;

    setIsClosing(true);
    try {
      const response = await databaseApi.closeAllConnections(database.dbname);
      if (response.success && response.data) {
        toast({
          title: "Success",
          description: response.data.message,
          status: "success",
        });
        // Refresh stats after closing connections
        await fetchConnectionStats();
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to close connections",
          status: "error",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to close connections",
        status: "error",
      });
    } finally {
      setIsClosing(false);
    }
  };

  /**
   * Cleans up idle connections
   */
  const handleCleanupIdleConnections = async () => {
    if (!database) return;

    setIsCleaning(true);
    try {
      const response = await databaseApi.cleanupIdleConnections(
        database.dbname
      );
      if (response.success && response.data) {
        toast({
          title: "Success",
          description: response.data.message,
          status: "success",
        });
        // Refresh stats after cleanup
        await fetchConnectionStats();
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to cleanup idle connections",
          status: "error",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cleanup idle connections",
        status: "error",
      });
    } finally {
      setIsCleaning(false);
    }
  };

  /**
   * Sets up auto-refresh when dialog is open
   */
  useEffect(() => {
    if (isOpen && database && autoRefresh) {
      // Fetch immediately
      fetchConnectionStats();

      // Set up interval to refresh every 5 seconds
      const interval = setInterval(() => {
        fetchConnectionStats();
      }, 5000);

      setRefreshInterval(interval);

      return () => {
        clearInterval(interval);
      };
    } else {
      // Clear interval when dialog closes or auto-refresh is disabled
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, database?.dbname, autoRefresh]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  if (!database) return null;

  const getDatabaseIcon = (dbtype: string) => {
    switch (dbtype) {
      case "MYSQL":
        return "🐬";
      case "POSTGRES":
        return "🐘";
      case "MONGODB":
        return "🍃";
      default:
        return "🗄️";
    }
  };

  return (
    <>
      <ToastContainer />
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{getDatabaseIcon(database.dbtype)}</span>
              <div>
                <DialogTitle>Connection Management</DialogTitle>
                <DialogDescription>
                  Manage active connections for {database.dbname}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Connections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-blue-500" />
                    <span className="text-2xl font-bold">
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        stats?.total || 0
                      )}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    SA Connections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-500" />
                    <span className="text-2xl font-bold">
                      {stats?.byUser.sa || 0}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    RW Connections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-green-500" />
                    <span className="text-2xl font-bold">
                      {stats?.byUser.rw || 0}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Idle Connections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-500" />
                    <span className="text-2xl font-bold">
                      {stats?.idle || 0}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Connection List */}
            {stats && stats.connections.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Active Connections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {stats.connections.map((conn, index) => (
                      <div
                        key={`${conn.id}-${index}`}
                        className="flex items-start justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">
                              {conn.user}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs ${
                                conn.state === "idle" || conn.state === "Sleep"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {conn.state}
                            </span>
                          </div>
                          {conn.query && (
                            <p className="text-xs text-muted-foreground font-mono truncate max-w-md">
                              {conn.query}
                            </p>
                          )}
                          {conn.duration !== undefined && (
                            <p className="text-xs text-muted-foreground">
                              Duration: {conn.duration}s
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          ID: {conn.id}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {stats && stats.connections.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No active connections
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                <span>Auto-refresh (5s)</span>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchConnectionStats}
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCleanupIdleConnections}
                disabled={isCleaning || !stats || stats.idle === 0}
              >
                {isCleaning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Cleanup Idle
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCloseAllConnections}
                disabled={isClosing || !stats || stats.total === 0}
              >
                {isClosing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Close All
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectionManagementDialog;
